import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { SiteContent } from '@/types/database.types'

/* ============================================================
   useSiteContent — PRD §8.1 CMS
   Read/write site_content rows grouped by section.
   useGlobalSave() for batch writes.
   ============================================================ */

/** All content for a tenant, grouped by section */
export function useSiteContent(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['site-content', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_content')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('section')

      if (error) throw error

      const rows = (data ?? []) as SiteContent[]
      // Group by section for easy access
      const grouped: Record<string, Record<string, string>> = {}
      for (const row of rows) {
        if (!grouped[row.section]) grouped[row.section] = {}
        grouped[row.section][row.content_key] = row.content_value
      }
      return { raw: rows, grouped }
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000, // 2 min (PRD §25)
  })
}

/** Get content for a specific section */
export function useSectionContent(tenantId: string | undefined, section: string) {
  return useQuery({
    queryKey: ['site-content', tenantId, section],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_content')
        .select('*')
        .eq('tenant_id', tenantId!)
        .eq('section', section)

      if (error) throw error

      const rows = (data ?? []) as SiteContent[]
      const map: Record<string, string> = {}
      for (const row of rows) {
        map[row.content_key] = row.content_value
      }
      return map
    },
    enabled: !!tenantId && !!section,
    staleTime: 2 * 60 * 1000,
  })
}

/** Batch save content changes — PRD §8.1 useGlobalSave */
export function useGlobalSave() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (
      changes: Array<{
        tenant_id: string
        section: string
        content_key: string
        content_value: string
      }>
    ) => {
      // Upsert all changes in one batch
      const { error } = await supabase
        .from('site_content')
        .upsert(changes as never, {
          onConflict: 'tenant_id,section,content_key',
        })

      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['site-content'] })
    },
  })
}
